import { useEffect, useState } from 'react'
import {
  WppButton,
  WppCheckbox,
  WppDivider,
  WppModal,
  WppTextareaInput,
  WppTypography,
} from '@wppopen/components-library-react'
import { ERROR_MESSAGES, MANUAL_UPLOAD_FILE_LIMIT_SIZE } from 'utils/constants'
import type { WppFileUploadCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { AcceptConfig, FileItemType, FileUploadEventDetail } from '@wppopen/components-library'
import modalStyles from '../Modal.module.scss'
import styles from './ManualUpload.module.scss'
import useApi from 'hooks/useApi'
import { getType, uploadFileToBucket } from 'utils'
import { ImportsCreate2Data, ImportsCreate2Payload, ImportsCreateResult } from '@contracts'
import FileUploader from 'components/FileUploader/FileUploader'
import WarningsView from './WarningsView'
import useCurrentUser from 'hooks/useCurrentUser'

const { MANDATORY_FIELD } = ERROR_MESSAGES
interface ManualUploadProps {
  shouldModalOpen: boolean
  onCancel: () => void
  onSuccessCb: () => void
  onErrorCb?: (err: Error) => void
}

function ManualUpload({ shouldModalOpen, onCancel, onSuccessCb, onErrorCb }: ManualUploadProps) {
  const [isApiLoading, setIsApiLoading] = useState(false)
  const [file, setFile] = useState<FileItemType[]>()
  const [isMarkedAsApproved, setIsMarkedAsApproved] = useState(false)
  const [comment, setComment] = useState('')
  const [errors, setErrors] = useState({
    file: '',
    approvedBy: '',
  })
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  const { fetchData: postJobsImport, url: postJobsImportUrl } = useApi<ImportsCreateResult>('jobs/imports')
  const { userName } = useCurrentUser()

  const {
    fetchData: postSingleJobImport,
    data: postSingleJobImportData,
    error: postSingleJobImportError,
  } = useApi<ImportsCreate2Data>('jobs/imports/{import_id}', {}, 0, 'json')

  // handle the file change
  const onFileChange = (e: WppFileUploadCustomEvent<FileUploadEventDetail>) => {
    const file = e.detail.value as FileItemType[]

    if (file?.length && file?.[0].size! > MANUAL_UPLOAD_FILE_LIMIT_SIZE) {
      setErrors(prev => ({ ...prev, file: 'File size should be less than 50MB' }))
      return
    }
    setFile(file)
    setErrors(prev => ({ ...prev, file: !file?.length ? MANDATORY_FIELD : '' }))
    setValidationErrors([])
  }

  // Function to upload the file using the pre-signed URL
  const uploadFile = async (data: ImportsCreateResult, file: File): Promise<Response | Error> => {
    if (!data) throw new Error('No import form data found')
    if (!data.import_form_data) throw new Error('No import form data found')
    if (!data.import_url) throw new Error('No import url found')
    const { import_form_data, import_url } = data

    try {
      const response = await uploadFileToBucket(import_form_data, file, import_url)
      if (!response.ok) throw new Error('Failed to upload file')
      return response
    } catch (error) {
      const err = error as Error
      console.error(err.message)
      return err
    }
  }

  // handle the form submit
  const handleSubmit = (e: Event) => {
    e.preventDefault()
    let isFormValid = true
    // check if there is errors in the form
    if (!file?.length) {
      setErrors(prev => ({ ...prev, file: MANDATORY_FIELD }))
      isFormValid = false
    }
    // if there is errors, return
    if (!isFormValid) return

    setIsApiLoading(true)
    postJobsImport(postJobsImportUrl, { method: 'POST' })
      .then(async res => {
        if (!res.import_url) throw new Error('No import url found')

        const awsRes = await uploadFile(res, file![0] as File)
        if (awsRes instanceof Error) {
          throw awsRes
        }
        return res
      })
      .then(async res => {
        const body = getType<ImportsCreate2Payload>({
          approve_job: isMarkedAsApproved,
          update_info: {
            user_name: userName,
            comment,
          },
        })

        if (!comment) delete body.update_info!.comment

        try {
          const result = await postSingleJobImport(`jobs/imports/${res.import_id}`, {
            method: 'POST',
            body: JSON.stringify(body) as BodyInit,
          })

          if (!result.warnings?.length) onSuccessCb()
        } catch (error) {
          const err = error as Error
          throw err
        }
      })
      .catch(error => {
        const err = error as Error
        console.error(err.message)
        if (onErrorCb) onErrorCb(err)
      })
      .finally(() => {
        setIsApiLoading(false)
      })
  }

  useEffect(() => {
    if (!postSingleJobImportError) return
    if (postSingleJobImportError.status !== 200) {
      setValidationErrors(postSingleJobImportError?.errors?.map(err => err.message) || [])
    }
  }, [postSingleJobImportError])

  return (
    <WppModal
      className={styles.modal}
      open={shouldModalOpen}
      formConfig={{
        onSubmit: handleSubmit,
        onReset: () => {
          onCancel()
        },
      }}
      data-testid="create-and-edit-modal"
    >
      <WppTypography tag="h3" type="xl-heading" slot="header">
        Upload
      </WppTypography>
      {postSingleJobImportData?.warnings?.length ? (
        <WarningsView warnings={postSingleJobImportData.warnings} onSuccessCb={onSuccessCb} />
      ) : (
        <>
          <div slot="body" className={styles.modal__body}>
            <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer" className={styles.modal__disclaimer}>
              Upload your corrected content below.
              <br />
              Note this will overwrite your content in the system.
            </WppTypography>

            <FileUploader
              onFileChange={onFileChange}
              fileValidation={validationErrors}
              messageType={validationErrors.length ? 'error' : undefined}
              multiple={false}
              name="upload-file"
              acceptConfig={getType<AcceptConfig>({
                'text/csv': ['.csv'],
              })}
              size={50}
              file={file?.length ? file[0] : undefined}
              message={errors.file}
            />

            <WppCheckbox
              required
              labelConfig={{ text: 'Mark content as approved' }}
              name="approved"
              checked={isMarkedAsApproved}
              onWppChange={() => setIsMarkedAsApproved(!isMarkedAsApproved)}
            />

            <WppDivider />

            <WppTextareaInput
              data-testid="comment-input"
              name="comment"
              placeholder="Enter your comment"
              labelConfig={{
                text: 'Comment',
              }}
              rows={3}
              value={comment}
              onWppChange={e => {
                setComment(e.detail.value || '')
              }}
            />
          </div>
          <div slot="actions" className={modalStyles.modal__actionsGroup}>
            <WppButton type="reset" variant="secondary" data-testid="cancel-modal">
              Cancel
            </WppButton>
            <WppButton type="submit" variant="primary" data-testid="submit-modal" loading={isApiLoading}>
              Upload
            </WppButton>
          </div>
        </>
      )}
    </WppModal>
  )
}
export default ManualUpload
